import { mainApi } from '../../../../api/mainApi';

import { setCampusList } from './campusSlice';

import { CampusResponse, CampusPayload, Campus, Company } from '../interfaces/campus.interfaces';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { AccessState } from '../../../../types';

export const campusTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Campus', 'CampussAvailables'],
});
// Define a service using a base URL and expected endpoints
export const campusApi = campusTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all campus
     */
    getCampus: builder.query<
      CampusResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, type_sort, sort }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<CampusResponse>('/campus', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              search,
            },
          });

          dispatch(setCampusList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Campus', id } as const)),
              { type: 'Campus', id: 'LIST' },
            ]
          : [{ type: 'Campus', id: 'LIST' }],
    }),

    /**
     * @GET all campus Availables
     */
    getAvailablesCampus: builder.query<Campus[], { profileId?: Number; statusId?: number }>({
      queryFn: async ({ profileId, statusId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Campus[]>('/campus', {
            params: {
              user_profile_id,
              ...(statusId && { status_id: statusId }),
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'CampussAvailables', id: 'COMBO_CAMPUS_LIST' }],
    }),

    /**
     * @GET campus by id
     */
    getCampusById: builder.query<Campus, { profileId?: number; campusId: number }>({
      queryFn: async ({ profileId, campusId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<Campus>(`/campus/${campusId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Campus', profileId }],
    }),

    /**
     * @POST create campus
     */
    addCampus: builder.mutation<
      Campus,
      { profileId: number; campusPayload: Partial<CampusPayload> }
    >({
      queryFn: async ({ profileId, campusPayload }) => {
        try {
          const { data } = await mainApi.post<Campus>('/campus', {
            user_profile_id: profileId,
            ...campusPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Campus', id: 'LIST' },
        { type: 'CampussAvailables', id: 'COMBO_CAMPUS_LIST' },
      ],
    }),

    /**
     * @GET campus list to combo box
     */
    getAvailableCompanies: builder.query<Company[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/companies', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @PUT update campus
     */
    updateCampus: builder.mutation<
      Campus,
      { profileId: number; campusId: number; campusPayload: Partial<Campus> }
    >({
      queryFn: async ({ profileId, campusId, campusPayload }) => {
        try {
          const { data } = await mainApi.put<Campus>(`/campus/${campusId}`, {
            user_profile_id: profileId,
            ...campusPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Campus', id: 'LIST' },
        { type: 'CampussAvailables', id: 'COMBO_CAMPUS_LIST' },
      ],
    }),

    /**
     * @DELETE remove campus
     */
    deleteCampus: builder.mutation<Campus, { profileId: number; campusId: number }>({
      queryFn: async ({ profileId, campusId }) => {
        try {
          const { data } = await mainApi.delete(`campus/${campusId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Campus', id: 'LIST' },
        { type: 'CampussAvailables', id: 'COMBO_CAMPUS_LIST' },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCampusQuery,
  useAddCampusMutation,
  useGetAvailableCompaniesQuery,
  useUpdateCampusMutation,
  useDeleteCampusMutation,
  useGetAvailablesCampusQuery,
  useGetCampusByIdQuery,
  useLazyGetCampusByIdQuery,
} = campusApi;
